import { createSlice } from "@reduxjs/toolkit";
const initialState = {
  currentPage: 1,
  searchQuery: "",
  languageSearchQuery: "",
  itemsPerPage: 1,
  africanOnly: false,
  syncChapters: true,
  selectedLanguages: ["Fulfulde (Benin) (Audio)","Yoruba (Audio)", "Nufi (Audio)"],
  chapters: [],
  totalPages: 333,
  maxItemsPerPage: 5,
  volume: 0.2,
  searchQueries: [{ q: "", sentence_ids: [] }],
  quickFilter: "all",
  recentLanguages: [],
};

const filterSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    selectAllLanguages: (state, action) => {
      // Access languages directly from the state
      const languages = action.payload;
      state.currentPage = 1;

      const allLanguageNames = languages.map((language) => language.name); // Extract the names of all languages
      state.selectedLanguages = allLanguageNames; // Select all languages
    },
    addOrUpdateSearchQuery: (state, action) => {
      const { q, sentence_ids } = action.payload;
      const existingQuery = state.searchQueries.find((query) => query.q === q);

      if (existingQuery) {
        existingQuery.sentence_ids = sentence_ids; // Update existing query
      } else {
        state.searchQueries.push({ q, sentence_ids }); // Add new query
      }
    },
    selectOnlyLanguages: (state, action) => {
      const purchasedLanguages = action.payload;
      state.selectedLanguages = purchasedLanguages;
    },
    deselectAllLanguages: (state) => {
      state.selectedLanguages = initialState.selectedLanguages; // Reset all languages
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setVolume: (state, action) => {
      state.volume = action.payload;
    },
    incrementPage: (state) => {
      if (state.currentPage < state.totalPages) {
        state.currentPage += 1;
      }
    },
    decrementPage: (state) => {
      if (state.currentPage > 1) {
        state.currentPage -= 1;
      }
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setLanguageSearchQuery: (state, action) => {
      state.languageSearchQuery = action.payload;
    },
    setQuickFilter: (state, action) => {
      state.quickFilter = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload;
    },
    setAfricanOnly: (state, action) => {
      state.africanOnly = action.payload;
    },
    toggleAfricanOnly: (state) => {
      state.africanOnly = !state.africanOnly;
    },
    setSyncChapters: (state, action) => {
      state.syncChapters = action.payload;
    },
    toggleSyncChapters: (state) => {
      state.syncChapters = !state.syncChapters;
    },
    // Toggle Selected Language: Adds if not already in array, removes if it is
    toggleSelectedLanguage: (state, action) => {
      const language = action.payload;
      state.currentPage = 1;
      if (state.selectedLanguages.includes(language)) {
        state.selectedLanguages = state.selectedLanguages.filter(
          (item) => item !== language
        );
      } else {
        state.selectedLanguages.push(language);
        const updatedRecent = [
          language,
          ...state.recentLanguages.filter((item) => item !== language),
        ];
        state.recentLanguages = updatedRecent.slice(0, 5);
      }
    },
    addChapter: (state, action) => {
      const { bookId, syncChapters } = action.payload;

      const chapterId =
        state.chapters.find((item) => item.book_id === bookId)?.id || 1;

      // Update the chapters array
      const updatedChapters = state.chapters.filter(
        (chapter) => chapter.book_id !== bookId
      );

      // Add the new chapter
      updatedChapters.push({ book_id: bookId, chapter_id: chapterId });

      // If syncChapters is true, set all chapters to the same chapter_id
      if (syncChapters) {
        state.chapters = updatedChapters.map((chapter) => ({
          ...chapter,
          chapter_id: chapterId,
        }));
      } else {
        state.chapters = updatedChapters;
      }
    },
    removeChapter: (state, action) => {
      const { chapter_id, book_id } = action.payload;
      state.chapters = state.chapters.filter(
        (chapter) =>
          !(chapter.chapter_id === chapter_id && chapter.book_id === book_id)
      );
    },
    resetSelectedLanguages: (state) => {
      state.selectedLanguages = [];
    },
    resetChapters: (state) => {
      state.chapters = [];
    },
    resetFilters: (state) => ({
      ...initialState, // Spread the initialState
      chapters: state.chapters, // Keep the current state of chapters unchanged
    }),
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    updateChapter: (state, action) => {
      const { book_id, chapter_id, syncChapters } = action.payload;

      if (syncChapters) {
        // Set all books to the same chapterId
        state.chapters = state.chapters.map((chapter) => ({
          ...chapter,
          chapter_id: chapter_id,
        }));
      } else {
        // Update only the specified bookId
        state.chapters = state.chapters
          .filter((chapter) => chapter.book_id !== book_id)
          .concat({ book_id: book_id, chapter_id: chapter_id });
      }
    },
  },
});

export const {
  setCurrentPage,
  updateChapter,
  resetFilters,
  toggleSyncChapters,
  decrementPage,
  incrementPage,
  setSearchQuery,
  setItemsPerPage,
  setVolume,
  setAfricanOnly,
  selectOnlyLanguages,
  toggleAfricanOnly,
  setSyncChapters,
  addChapter,
  removeChapter,
  resetChapters,
  resetSelectedLanguages,
  toggleSelectedLanguage,
  setLanguageSearchQuery,
  setQuickFilter,
  setTotalPages,
  selectAllLanguages,
  deselectAllLanguages,
} = filterSlice.actions;

export default filterSlice.reducer;
